import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DeleteButton from '../components/DeleteButton';
import ImageViewer from '../components/ImageViewer';
import PageNav, { buildTrail } from '../components/PageNav';
import QrSection from '../components/QrSection';
import './ItemDetail.css';

function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [location, setLocation] = useState(null);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItemData();
  }, [id]);

  const fetchItemData = async () => {
    try {
      const itemRes = await fetch(`/api/items/${id}`);
      if (itemRes.status === 404) {
        // A scanned QR address carries the record's route code, not its id; resolve it
        const byRoute = await fetch(`/api/items/route/${id}`);
        if (byRoute.ok) {
          const record = await byRoute.json();
          // Opening a label's address means "where does this go?", so start the guide
          navigate(record.storageLocationId ? `/guide/items/${record.id}` : `/items/${record.id}`, { replace: true });
          return;
        }
      }
      if (!itemRes.ok) {
        setItem(null);
        setLoading(false);
        return;
      }
      const itemData = await itemRes.json();
      setItem(itemData);

      if (itemData.storageLocationId) {
        const locRes = await fetch(`/api/locations/${itemData.storageLocationId}`);
        const locData = await locRes.json();
        setLocation(locData);

        const allRes = await fetch('/api/locations/all');
        if (allRes.ok) setTrail(buildTrail(await allRes.json(), itemData.storageLocationId));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching item data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="item-detail"><p>Loading...</p></div>;
  }

  if (!item) {
    return <div className="item-detail"><p>Item not found</p></div>;
  }

  return (
    <div className="item-detail">
      <PageNav
        trail={trail}
        current={item.name}
        fallback={item.storageLocationId ? `/locations/${item.storageLocationId}` : '/items'}
      />
      <div className="detail-header">
        <div className="header-content">
          {item.image && (
            <ImageViewer
              src={`data:${item.imageMimeType};base64,${item.image}`}
              alt={item.name}
              className="detail-image"
            />
          )}
          <div>
            <h1>{item.name}</h1>
            <p className="description">{item.description}</p>
            <div className="item-meta">
              <span className="meta-item"><strong>Count:</strong> {item.count}</span>
              {location && (
                <span className="meta-item">
                  <strong>Location:</strong> <Link to={`/locations/${location.id}`}>{location.name}</Link>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          {item.storageLocationId && (
            <Link to={`/guide/items/${id}`} className="btn btn-primary">Guide me</Link>
          )}
          <Link to={`/items/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <DeleteButton
            url={`/api/items/${id}`}
            noun="item"
            className="btn btn-danger"
            onDeleted={() => navigate(item && item.storageLocationId ? `/locations/${item.storageLocationId}` : '/items')}
          />
        </div>
      </div>

      <QrSection qrCode={item.qrCode} name={item.name} noun="item" />
    </div>
  );
}

export default ItemDetail;
